/**
 * Directorio unificado: personas, accesos, grupos y roles en una sola pantalla.
 */
import React, { useEffect, useMemo, useState } from 'react';
import UserManagement from './UserManagement';
import GroupManagement from './GroupManagement';
import RoleManagement from './RoleManagement';
import PermissionManagement from './PermissionManagement';
import { studio, StudioTabs } from './studioUi';

export const PEOPLE_HUB_VIEWS = ['usuarios', 'grupos', 'roles', 'permisos'];

const PeopleHub = ({
    initialTab = 'usuarios',
    styles,
    userPermissions,
    users,
    roles,
    groups,
    setGroups,
    selectedGroup,
    setSelectedGroup,
    groupMembers,
    setGroupMembers,
    groupPermissions,
    setGroupPermissions,
    dashboards,
    cargarTodo,
    fetchWithAuth,
    API_URL,
    areasList,
    onImpersonate,
    findUserName,
    allPermissionsList,
    permisosActuales,
    setPermisosActuales,
    selectedUserForPermissions,
    setSelectedUserForPermissions,
}) => {
    const tabs = useMemo(() => {
        const all = [
            {
                id: 'usuarios',
                label: 'Personas',
                hint: 'Cuentas, empresa y rol asignado',
                perm: 'admin_panel_users_view',
                count: users?.length || 0,
            },
            {
                id: 'permisos',
                label: 'Accesos',
                hint: 'Asigna reportes concretos a una persona',
                perm: 'admin_panel_permissions_view',
                count: new Set((permisosActuales || []).map((perm) => perm.userId).filter(Boolean)).size,
            },
            {
                id: 'grupos',
                label: 'Grupos',
                hint: 'Equipos y accesos compartidos a reportes',
                perm: 'admin_panel_groups_view',
                count: groups?.length || 0,
            },
            {
                id: 'roles',
                label: 'Roles',
                hint: 'Permisos del sistema por perfil',
                perm: 'admin_panel_roles_view',
                count: roles?.length || 0,
            },
        ];
        return all.filter((tab) => userPermissions?.[tab.perm]);
    }, [userPermissions, users, groups, roles, permisosActuales]);

    const tabIds = tabs.map((item) => item.id).join(',');

    const [tab, setTab] = useState(() => (
        tabs.some((item) => item.id === initialTab) ? initialTab : (tabs[0]?.id || 'usuarios')
    ));

    useEffect(() => {
        if (tabs.some((item) => item.id === initialTab)) setTab(initialTab);
    }, [initialTab, tabIds, tabs]);

    useEffect(() => {
        if (tabs.length && !tabs.some((item) => item.id === tab)) {
            setTab(tabs[0].id);
        }
    }, [tabs, tab]);

    const active = tabs.find((item) => item.id === tab);

    const openAccessForUser = (user) => {
        setSelectedUserForPermissions(user);
        setTab('permisos');
    };

    return (
        <div style={studio.page}>
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Directorio</p>
                    <h2 style={studio.title}>Gestión de usuarios</h2>
                    <p style={studio.subtitle}>
                        Personas, accesos a reportes, grupos y roles en un mismo lugar.
                    </p>
                </div>
                <div style={studio.stats}>
                    {tabs.map((item) => (
                        <div key={item.id} style={studio.stat}>
                            <span style={studio.statValue}>{item.count}</span>
                            <span style={studio.statLabel}>{item.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            <StudioTabs items={tabs} value={tab} onChange={setTab} />
            {active && <p style={studio.hint}>{active.hint}</p>}

            {tab === 'usuarios' && userPermissions.admin_panel_users_view && (
                <UserManagement
                    embedded
                    users={users}
                    cargarTodo={cargarTodo}
                    onImpersonate={onImpersonate}
                    styles={styles}
                    fetchWithAuth={fetchWithAuth}
                    API_URL={API_URL}
                    userPermissions={userPermissions}
                    areasList={areasList}
                    roles={roles}
                    onManageAccess={userPermissions.admin_panel_permissions_view ? openAccessForUser : undefined}
                />
            )}
            {tab === 'permisos' && userPermissions.admin_panel_permissions_view && (
                <PermissionManagement
                    embedded
                    users={users}
                    biblioteca={dashboards}
                    permisosActuales={permisosActuales}
                    setPermisosActuales={setPermisosActuales}
                    selectedUserForPermissions={selectedUserForPermissions}
                    setSelectedUserForPermissions={setSelectedUserForPermissions}
                    cargarTodo={cargarTodo}
                    fetchWithAuth={fetchWithAuth}
                    API_URL={API_URL}
                    styles={styles}
                    userPermissions={userPermissions}
                />
            )}
            {tab === 'grupos' && userPermissions.admin_panel_groups_view && (
                <GroupManagement
                    embedded
                    groups={groups}
                    setGroups={setGroups}
                    selectedGroup={selectedGroup}
                    setSelectedGroup={setSelectedGroup}
                    groupMembers={groupMembers}
                    setGroupMembers={setGroupMembers}
                    groupPermissions={groupPermissions}
                    setGroupPermissions={setGroupPermissions}
                    users={users}
                    biblioteca={dashboards}
                    cargarTodo={cargarTodo}
                    API_URL={API_URL}
                    fetchWithAuth={fetchWithAuth}
                    styles={styles}
                    userPermissions={userPermissions}
                    findUserName={findUserName}
                />
            )}
            {tab === 'roles' && userPermissions.admin_panel_roles_view && (
                <RoleManagement
                    embedded
                    roles={roles}
                    allPermissionsList={allPermissionsList}
                    cargarTodo={cargarTodo}
                    API_URL={API_URL}
                    styles={styles}
                    fetchWithAuth={fetchWithAuth}
                />
            )}
        </div>
    );
};

export default PeopleHub;
